"use client"
import { type CSSProperties, useCallback, useRef } from "react"
import cn from "classnames"

import { AnimateText } from "~/components/elements/AnimateText/AnimateText"
import { FadeIn } from "~/components/elements/FadeIn/FadeIn"
import { Button } from "~/components/primitives/Button/Button"
import { HtmlRenderer } from "~/components/primitives/HtmlRenderer"
import { ResponsiveAsset } from "~/components/primitives/ResponsiveAsset/ResponsiveAsset"
import { TextWithPill } from "~/components/primitives/TextWithPill/TextWithPill"
import { useStickyHeroFade } from "~/hooks/useStickyHeroFade"
import { getModuleConfigProps } from "~/utils/moduleConfig"

import type { HeroInterface } from "./Hero.interface"
import s from "./Hero.module.scss"

export type HeroViewProps = Omit<HeroInterface, "id"> & {
  moduleIndex: number
  className?: string
  style?: CSSProperties
}

/**
 * Presentational component for Hero module.
 * Use this for Storybook stories and testing.
 * The main Hero component is an async Server Component that fetches CMS data.
 */
export const HeroView = ({
  moduleIndex,
  headline,
  pillHeadline,
  pill,
  subheadline,
  content,
  asset,
  ctas,
  config,
  className,
  style,
}: HeroViewProps) => {
  const configProps = getModuleConfigProps(config)
  const heroElRef = useRef<HTMLDivElement>(null)
  const heroRef = useCallback((node: HTMLDivElement | null) => {
    heroElRef.current = node
    if (node) {
      const isFirstChild = node.parentElement?.firstElementChild === node
      if (isFirstChild) {
        node.parentElement?.parentElement?.setAttribute("data-has-hero", "true")
      }
    }
  }, [])

  useStickyHeroFade(heroElRef)

  return (
    <section
      ref={heroRef}
      className={cn(s.hero, `stack-module-${moduleIndex}`, className)}
      data-module="hero"
      {...configProps}
      style={{
        ...configProps.style,
        ...style,
      }}
    >
      {asset?.desktop && (
        <ResponsiveAsset
          className={s.backgroundAsset}
          desktop={asset.desktop}
          mobile={asset.mobile ?? undefined}
          imageSizes={{ base: "100vw" }}
        />
      )}
      <div className={cn("container", "default-grid")}>
        <div className={s.heroContainer}>
          <header>
            {headline && (
              <AnimateText element="h1" className={s.headline}>
                <HtmlRenderer inline html={headline} />
              </AnimateText>
            )}
            {pillHeadline && !headline && (
              <AnimateText element="div" className={s.headline}>
                <TextWithPill content={pillHeadline} pill={pill} />
              </AnimateText>
            )}
            {subheadline && (
              <AnimateText element="h3" delay={0.2}>
                <HtmlRenderer inline html={subheadline} />
              </AnimateText>
            )}
          </header>
          <FadeIn element="div" className={s.content} delay={0.5}>
            {content && <HtmlRenderer animate html={content} />}
            {ctas && ctas.length > 0 && (
              <ul className={s.ctaList}>
                {ctas.map((cta, index) => (
                  <li key={index}>
                    <Button
                      className={s.cta}
                      cta={cta}
                      theme={configProps["data-typography"] || "light"}
                    />
                  </li>
                ))}
              </ul>
            )}
          </FadeIn>
        </div>
      </div>
    </section>
  )
}
